<template>
  <div
    v-if="details"
    class="flex flex-col gap-5 text-base"
    :class="allowOverflow && 'overflow-x-auto'"
  >
    <table
      class="w-full leading-none sm:table-fixed"
      :style="{ 'min-width': minWidth }"
    >
      <thead v-if="columns">
        <tr>
          <th class="w-[45px] pb-2 text-left text-gray-550">
            <span class="text-[15px] font-normal leading-5">
              {{ $t('rx.data_table.eye') }}
            </span>
          </th>
          <th
            v-for="(label, i) in columns"
            :key="`col-${i}`"
            class="pb-2 text-left text-gray-550"
          >
            <span class="text-[15px] font-normal leading-5">
              {{ $t(`rx.data_table.${label}`) }}
            </span>
          </th>
        </tr>
      </thead>
      <tbody v-if="tableDataRows" :class="border">
        <tr
          v-for="[eye, eyeDetails] in tableDataRows"
          :key="`row-${eye}`"
          :data-testid="`eye-${eye}`"
        >
          <td class="py-2">
            <span class="text-base font-semibold leading-5">
              {{
                eye === 'left'
                  ? $t(`rx.left_eye_label`)
                  : $t(`rx.right_eye_label`)
              }}
            </span>
          </td>
          <td
            v-for="[label, value] in Object.entries(eyeDetails)"
            :key="`key-${label}`"
            :data-testid="label"
            class="py-2"
          >
            <span class="text-base leading-5">
              {{ value }}
            </span>
          </td>
        </tr>
      </tbody>
    </table>
    <div v-if="pdValues">
      <table class="w-full table-fixed">
        <tbody>
          <tr
            v-for="[eye, value] in pdValues"
            :key="`row-${eye}`"
            :data-testid="`pd-${eye}`"
          >
            <td class="w-[17ch]">
              <span class="flex w-full justify-between text-[15px] leading-5">
                {{
                  $t(`rx.data_table.${eye === 'left' ? 'pd_left' : 'pd_right'}`)
                }}
              </span>
            </td>
            <td>{{ Number.isNaN(value) ? '-' : value }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
  <div v-else class="flex gap-5 rounded-lg bg-gray-350 p-3">
    <div class="flex flex-col justify-start">
      <div class="rounded-full bg-white p-2">
        <LazyIconFielmannEye class="size-10" />
      </div>
    </div>
    <span class="flex flex-col justify-center pr-5">
      {{ $t('rx.data_table.no_table_text') }}
    </span>
  </div>
</template>
<script setup lang="ts">
const props = defineProps({
  details: {
    type: Object as PropType<CorrectionalPrescriptionValues>,
    default: undefined,
  },
  allowOverflow: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  minWidth: {
    type: String as PropType<string>,
    default: '',
  },
  skipEmpty: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  border: {
    type: String as PropType<string>,
    default: 'border-y border-[#D7DDE3]',
  },
})

const $currentShop = useCurrentShop()

const tableData = computed(() => {
  if (props.details?.left && isRxPrescriptionValue(props.details.left)) {
    const formattedValues = formatSightSpecificationForDisplay(
      props.details as RxPrescriptionValues,
      $currentShop.value,
      true,
    )
    const { pd: leftPd, ...left } = formattedValues.left || {}
    const { pd: rightPd, ...right } = formattedValues.right || {}

    return {
      rows: { right, left },
      pdValues: {
        right: rightPd,
        left: leftPd,
      },
    }
  } else {
    const formattedValues = formatSightSpecificationForDisplay(
      props.details as Partial<ContactLensValues>,
      $currentShop.value,
      true,
    )
    const displayValues = {
      right: formattedValues.right || {},
      left: formattedValues.left || {},
    }
    const orderedVals = {
      right: {} as typeof displayValues.right,
      left: {} as typeof displayValues.left,
    }
    contactLensProps.forEach((contactLensProp) => {
      if (
        contactLensProp !== 'diameter' &&
        (displayValues.left[contactLensProp] ||
          displayValues.right[contactLensProp])
      ) {
        orderedVals.left[contactLensProp] =
          displayValues.left[contactLensProp] ?? '-'
        orderedVals.right[contactLensProp] =
          displayValues.right[contactLensProp] ?? '-'
      }
    })

    return { rows: orderedVals }
  }
})

const tableDataRows = computed(() => {
  if (tableData?.value?.rows && props.skipEmpty) {
    return Object.entries(tableData.value.rows).filter(
      ([key]) => key in (props?.details ?? {}),
    )
  } else if (tableData?.value?.rows) {
    return Object.entries(tableData.value.rows)
  }
  return undefined
})

const columns = computed(() => {
  if (tableData.value?.rows?.left) {
    return Object.keys(tableData.value.rows.left)
  }
  return undefined
})

const pdValues = computed(() => {
  if (tableData?.value?.pdValues) {
    return Object.entries(tableData.value.pdValues)
  }
  return undefined
})
</script>
